import { useMutation } from '@apollo/client'
import get from 'lodash/get'
import { useSelector } from 'react-redux'

import {
  CreatePaymentInvoiceDocument,
  SendInvoicesForOrdersDocument,
} from '__generated__/atlas-types'

import { fromGlobalId, toGlobalId } from 'utils/transformations/graphql'

import { atlasClient } from 'graphql/client'
import { atlas } from 'graphql/tags'
import { getAccountId, getCurrentUser } from 'store/currentUser/selectors'

atlas`#graphql
  mutation SendInvoicesForOrders($input: SendInvoicesForOrdersInput!) {
    sendInvoicesForOrders(input: $input) {
      orderId
      sentTo
      sentOn
    }
  }
`

atlas`#graphql
  mutation CreatePaymentInvoice($input: CreateInvoicesInput!) {
    createInvoices(input: $input) {
      orderId
      invoiceId
    }
  }
`

type Order = {
  id: string
  webOrderLookupId?: string
  shippingEmail?: string
  referenceNumber: string
  retailerName?: string
  billingAddress?: string
  retailerEmail?: string
  currency?: string
  amount?: string
}

export function useSendInvoicesForOrders() {
  const accountId = useSelector(getAccountId)
  const currentUser = useSelector(getCurrentUser)

  const [mutate] = useMutation(SendInvoicesForOrdersDocument, {
    client: atlasClient,
  })
  return {
    sendInvoicesForOrders: (order: Order, severalEmails?: string[]) => {
      const brandContactName = currentUser.name || currentUser.email || '--'
      return mutate({
        variables: {
          input: {
            accountId: toGlobalId('Account', accountId) ?? '',
            invoicesParameters: [
              {
                orderId: order.id,
                webOrderId:
                  toGlobalId('OrderWeb', order.webOrderLookupId) ?? '',
                destinationAccountId:
                  toGlobalId(
                    'Account',
                    fromGlobalId(get(order, 'retailer.id')).id,
                  ) ?? '',
                orderBrandId:
                  toGlobalId(
                    'Account',
                    fromGlobalId(get(order, 'brand.id')).id,
                  ) ?? '',
                poNumber: order.referenceNumber,
                brandName: get(currentUser, 'account.profileName'),
                brandUsername: currentUser.name || currentUser.email,
                sentFrom: currentUser.email,
                sentBcc: severalEmails,
                sentTo: order.shippingEmail,
                language: currentUser.emailLanguage,
                ...(order.billingAddress && {
                  billingAddress: order.billingAddress,
                }),
                ...(order.retailerName && { retailerName: order.retailerName }),
                ...(order.retailerEmail && {
                  retailerEmail: order.retailerEmail,
                }),
                ...(brandContactName && { brandContactName }),
                ...(order.currency && { currency: order.currency }),
                ...(order.amount && { amount: order.amount }),
              },
            ],
          },
        },
      })
    },
  }
}

export function useCreatePaymentInvoiceForOrders() {
  const [mutate] = useMutation(CreatePaymentInvoiceDocument, {
    client: atlasClient,
  })
  return {
    createPaymentInvoiceForOrders: (orderIds: string[], retailerId: string) => {
      return mutate({
        variables: {
          input: {
            orderIds: orderIds,
            retailerId,
          },
        },
      })
    },
  }
}
